import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Calendar, 
  Users, 
  Settings, 
  Gift,
  Car,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building2,
  Wallet,
  Lock,
  KeyRound,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import NotificationBell from '../components/NotificationBell';
import PushPermissionBanner from '../components/PushPermissionBanner';
import ChangePinModal from '../components/auth/ChangePinModal';

export default function DashboardLayout({ children }) {
  const { user, logout, isAdmin, isCajero, isOperador, isGerente } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Define navigation items based on role
  const navItems = [];
  
  if (isAdmin) {
    navItems.push(
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/pos', icon: ShoppingCart, label: 'POS / Caja' },
      { path: '/agenda', icon: Calendar, label: 'Agenda' },
      { path: '/admin/clientes', icon: Users, label: 'Clientes' },
      { path: '/admin/servicios', icon: Car, label: 'Servicios' },
      { path: '/admin/usuarios', icon: Users, label: 'Usuarios' },
      { path: '/admin/recompensas', icon: Gift, label: 'Recompensas' },
      { path: '/admin/sucursales', icon: Building2, label: 'Sucursales' },
      { path: '/admin/finanzas', icon: Wallet, label: 'Finanzas' },
      { path: '/admin/caja', icon: Lock, label: 'Historial Caja' },
      { path: '/admin/detailing', icon: Sparkles, label: 'Detailing' },
    );
  } else if (isGerente) {
    navItems.push(
      { path: '/pos', icon: ShoppingCart, label: 'POS / Caja' },
      { path: '/agenda', icon: Calendar, label: 'Agenda' },
      { path: '/admin/clientes', icon: Users, label: 'Clientes' },
      { path: '/admin/caja', icon: Lock, label: 'Historial Caja' },
      { path: '/admin/ventas-diarias', icon: Wallet, label: 'Ventas Diarias' },
    );
  } else if (isCajero) {
    navItems.push(
      { path: '/pos', icon: ShoppingCart, label: 'POS / Caja' },
      { path: '/agenda', icon: Calendar, label: 'Agenda' },
      { path: '/admin/clientes', icon: Users, label: 'Clientes' },
    );
  } else if (isOperador) {
    navItems.push(
      { path: '/agenda', icon: Calendar, label: 'Agenda' },
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png" alt="WD Logo" className="w-8 h-8 object-contain" />
            <span className="font-bold text-white">WD Lavado</span>
          </div>
        </div>
        <div className="text-white text-sm">
          {user?.name}
        </div>
      </div>

      {/* Sidebar */}
      <aside className={cn(
        "sidebar",
        sidebarOpen && "open"
      )}>
        {/* Logo */}
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-700 shrink-0">
          <img src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png" alt="WD Logo" className="w-8 h-8 object-contain" />
          <span className="font-bold text-base">WD Lavado</span>
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-b border-slate-700 shrink-0 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-400">Bienvenido</div>
            <div className="font-semibold text-sm truncate">{user?.name}</div>
            <div className="text-xs text-blue-400 uppercase">{user?.role}</div>
          </div>
          {isAdmin && <NotificationBell />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn("sidebar-link", isActive && "active")}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
                {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="shrink-0 p-3 border-t border-slate-700 space-y-1">
          {isAdmin && (
            <button
              onClick={() => setShowPinModal(true)}
              className="sidebar-link w-full text-slate-300 hover:text-white hover:bg-slate-700/50"
              data-testid="change-pin-btn"
            >
              <KeyRound className="w-4 h-4" />
              <span>Cambiar PIN</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/30"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>

      {/* One-time push permission prompt for admins (PWA / Android / desktop) */}
      {isAdmin && <PushPermissionBanner />}
      {isAdmin && <ChangePinModal open={showPinModal} onOpenChange={setShowPinModal} />}
    </div>
  );
}
